import { PropType } from 'vue';
import { mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  EPDeviceDTO,
  EPDeviceTypeEnum,
  EntrancePlantDetailDTO,
  SwitchboardConfigDTO,
  SwitchboardDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { isNumber, isString } from '@microsoft/applicationinsights-core-js';

@Options({
  name: 'DialogAddEditSwitchboard',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    switchboardToEdit: {
      type: Object as PropType<SwitchboardDTO>,
      required: false,
    },
  },
  computed:{
    ...mapState(['currentEntrancePlant'])
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.isValidScs = true;
        this.invalidScsReason = '';
        this.switchboardDevicesIds = [];
        this.initSwitchboard();
      }
    },
    'newSwitchboard.switchboard.name': function () {
      if (this.newSwitchboard.switchboard) {
        this.verifyValidityOfName();
      }
    },
    'newSwitchboard.switchboard.scsAddr': function () {
      if (this.newSwitchboard.switchboard) {
        this.verifyValidityOfScsAddress();
      }
    },
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditSwitchboard extends Vue {
  newSwitchboard: SwitchboardConfigDTO = new SwitchboardConfigDTO();
  currentEntrancePlant!: EntrancePlantDetailDTO;
  switchboardToEdit!: SwitchboardDTO;
  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;
  readonly nameMaxLength = 50;
  switchboardDevicesIds: string[] = [];

  // Naming validation props
  isValidName = true;
  isValidScs = true;
  invalidNameReason: string = '';
  invalidScsReason: string = '';

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  async initSwitchboard() {
    if (this.switchboardToEdit) {
      this.isEdit = true;
      this.newSwitchboard.switchboard = new SwitchboardDTO(
        this.switchboardToEdit
      );
      this.newSwitchboard.devices = await this.checkIfSwitchboardHasDevices();
    } else {
      this.isEdit = false;
      this.newSwitchboard.switchboard = new SwitchboardDTO();
      this.newSwitchboard.switchboard.scsAddr = this.currentEntrancePlant.scsAddressHelper?.firstAvailableScsAddressSwitchboard;
      this.newSwitchboard.devices = [];
      this.newSwitchboard.switchboard.name = this.getSwitchboardName();
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.SwitchboardEditTitle')
      : this.$t('AppResourceEP.SwitchboardAddTitle');
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get devices() {
    const deviceList = this.fillDeviceList();
    return deviceList;
  }

  get saveButtonDisabled() {
    const disabledForNameCheck =
      !this.isValidName || !this.newSwitchboard.switchboard?.name?.trim();
    const disabledForScsCheck = !this.isValidScs;
    return disabledForNameCheck || disabledForScsCheck;
  }

  fillDeviceList() {
    if (this.currentEntrancePlant.gates === undefined) return [];
    const EPDeviceList: {
      device: EPDeviceDTO;
      gate: string;
    }[] = [];
    const gatesWithLinea5000 = this.currentEntrancePlant.gates.filter(
      (gate) => gate.devices?.some(
        (device) => device.type === EPDeviceTypeEnum.Linea5000
      ));
    
    for (const gate of gatesWithLinea5000) {
      if (gate.devices) {
        for (const device of gate.devices) {
          EPDeviceList.push({
            device: device,
            gate: gate.name ?? '',
          });
        }
      }
    }
    return EPDeviceList;
  }

  async checkIfSwitchboardHasDevices() {
    if (this.currentEntrancePlant.gates === undefined) return [];
    const gates = this.currentEntrancePlant.gates;
    let selectedDevices: EPDeviceDTO[] = [];
    for (const gate of gates) {
      if (gate.devices) {
        for (const device of gate.devices) {
          if (
            device.switchboardId ===
            this.newSwitchboard.switchboard?.switchboardId
          ) {
            selectedDevices.push(device);
            this.switchboardDevicesIds.push(device.deviceId!);
          }
        }
      }
    }
    return selectedDevices;
  }

  getSwitchboardName() {
    let baseName = `${this.$t('AppResourceEP.Switchboard')}`
    let name ='';
    let i = 0;
    do {
      i++;
      name = `${baseName} ${(this.currentEntrancePlant.switchboards?.length ?? 0) + i}`;
    } while (this.alreadyExist(name));

    return name;
  }

  alreadyExist(value: string | number): boolean {
    let listToCheck = this.currentEntrancePlant.switchboards || [];
    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (switchb) => switchb.switchboardId !== this.switchboardToEdit.switchboardId
      );
    }
    if (isString(value)) {
      const nameExist = listToCheck.some(
        (existing) => existing.name?.toLowerCase().trim() === (value as string).toLowerCase().trim()
      );
      return nameExist;
    } else if(isNumber(value)) {
      const addrExist = listToCheck.some(
        (existing) => existing.scsAddr === value as number 
      );
      return addrExist;
    }
    return true
  }

  verifyValidityOfName() {
    if (this.newSwitchboard.switchboard?.name === undefined) return;

    this.newSwitchboard.switchboard.name = this.newSwitchboard.switchboard.name.trim();
    this.isValidName = !this.alreadyExist(this.newSwitchboard.switchboard.name);
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  verifyValidityOfScsAddress() {
    if (this.newSwitchboard.switchboard?.scsAddr === undefined) return false;
    
    const value = Number(this.newSwitchboard.switchboard.scsAddr);

    if (this.isEdit && this.switchboardToEdit.scsAddr === value) {
      this.isValidScs = true;
      this.invalidScsReason = '';
      return;
    }
    const acceptedRange = value >= 0 && value <= 15;
    const isItAvailable = this.currentEntrancePlant.scsAddressHelper?.availableScsAddressSwitchboard?.includes(value) ?? false;
    const doesNotExist = !this.alreadyExist(value)
    this.isValidScs = acceptedRange && doesNotExist && isItAvailable;
    if (!acceptedRange) {
      this.invalidScsReason = this.$t('AppResourceEP.SwitchboardScsAddressOutOfRange');
    } else if (!doesNotExist) {
      this.invalidScsReason = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
    } else if (!isItAvailable) {
      this.invalidScsReason = this.$t('AppResourceEP.InputNotValid');
    } else {
      this.invalidScsReason = '';
    }
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;

    await this.saveSwitchboard();

    this.$emit('update-list');   
  }

  async saveSwitchboard() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.newSwitchboard.devices = this.devices
        .filter((device) => this.switchboardDevicesIds.includes(device.device.deviceId!))
        .map((device) => device.device);

      if (this.isEdit) {
        await plantsClient.switchboardPUT(
          this.currentEntrancePlant.desktopId,
          new SwitchboardConfigDTO(this.newSwitchboard)
        );
      } else {
        await plantsClient.switchboardPOST(
          this.currentEntrancePlant.desktopId,
          this.newSwitchboard
        );
      }

      this.$toast.add({
        severity: 'success',
        summary: this.isEdit
          ? this.$t('entrancePlant.editedSwitchboard')
          : this.$t('AppResourceEP.SwitchboardBannerCreated'),
        closable: false,
        life: 3000,
      });

    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: false,
          life: 3000,
        });
      }, 1000);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
